import { Helmet } from 'react-helmet-async';

export default function PrivacyPolicyPage() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | MrJoe.Pro Data Protection & Privacy Practices</title>
        <meta name="description" content="Read MrJoe.Pro's Privacy Policy by Buildmetric LLC. Learn how we collect, use, and protect your data, including CCPA rights for California residents. Last updated February 15, 2025." />
        <meta name="keywords" content="MrJoe.Pro privacy policy, data protection, CCPA, California privacy rights, Buildmetric privacy" />
        <link rel="canonical" href="https://mrjoe.pro/pub/privacy_policy" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mrjoe.pro/pub/privacy_policy" />
        <meta property="og:title" content="Privacy Policy | MrJoe.Pro" />
        <meta property="og:description" content="Learn how MrJoe.Pro protects your data and respects your privacy rights." />
        
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Privacy Policy | MrJoe.Pro" />
        <meta name="twitter:description" content="Learn how MrJoe.Pro protects your data and respects your privacy rights." />
      </Helmet>

      <section className="page-padding">
        <div className="container-safe">
          <div className="mx-auto max-w-[1008px] text-lg tracking-tight">
            <h1 className="page-heading">Privacy Policy for MrJoe.pro</h1>

            {/* Updated */}
            <div className="mt-7">
              <p className="text-xl font-medium">Last Updated: February 15, 2025</p>
              <p className="mt-2">
                This Privacy Policy ("Policy") explains how Buildmetric LLC ("Company," "we," "our,"
                or "us") collects, uses, discloses, and protects information collected from or about
                you through MrJoe.pro website and mobile applications (collectively, the "Services").
              </p>
            </div>

            {/* Content */}
            <article className="mt-10">
              {/* 1 */}
              <h2 className="text-xl font-medium">1. INFORMATION WE COLLECT</h2>
              <p className="mt-2">
                We collect information directly from you, automatically through your use of the
                Services, and from other sources.
              </p>
              <p className="mt-4">Information You Provide Directly:</p>
              <ul className="list-disc pl-6">
                <li>Contact information (name, email, phone number, postal address)</li>
                <li>Account credentials (username and password)</li>
                <li>Business information (company name, business address, license numbers)</li>
                <li>Payment information (processed by third-party payment processors)</li>
                <li>Customer information you provide for estimates and invoices</li>
                <li>Project photos and descriptions</li>
                <li>Employee time card data</li>
              </ul>
              <p className="mt-4">Information That May Be Collected:</p>
              <ul className="list-disc pl-6">
                <li>Device information (IP address, browser type, operating system)</li>
                <li>Usage data (pages visited, features used, time spent on Services)</li>
                <li>Location data (when using mobile applications, if permitted)</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>

              {/* 2 */}
              <h2 className="mt-6 text-xl font-medium">2. HOW WE USE YOUR INFORMATION</h2>
              <p className="mt-2">We use your information for the following purposes:</p>
              <ul className="list-disc pl-6">
                <li>Provide and operate the Services</li>
                <li>Process payments and transactions</li>
                <li>Send notifications about estimates, invoices, and messages to your customers</li>
                <li>Communicate with you about our Services</li>
                <li>Improve and develop our Services</li>
                <li>Protect against fraud and unauthorized access</li>
                <li>Comply with legal obligations</li>
              </ul>
              <p className="mt-2">Communication Practices:</p>
              <p>MrJoe.pro is committed to responsible communication practices. We:</p>
              <ul className="list-disc pl-6">
                <li>Will not send spam SMS text messages or emails</li>
                <li>Only send communications that are directly related to our Services</li>
                <li>Always include an option to opt-out of communications</li>
                <li>Respect your communication preferences</li>
                <li>Only contact your customers with information related to services you provide</li>
              </ul>

              {/* 3 */}
              <h2 className="mt-6 text-xl font-medium">3. DISCLOSURE OF YOUR INFORMATION</h2>
              <p className="mt-2">We may share your information with:</p>
              <ul className="list-disc pl-6">
                <li>Service providers (payment processors, email services, cloud storage)</li>
                <li>Your authorized employees and contractors</li>
                <li>Your customers (limited to information necessary for service delivery)</li>
                <li>Law enforcement or regulatory authorities when required by law</li>
                <li>Business partners (only with your consent)</li>
              </ul>

              {/* 4 */}
              <h2 className="mt-6 text-xl font-medium">4. DATA SECURITY</h2>
              <p className="mt-2">
                We implement appropriate technical and organizational measures to protect your
                information. However, no Internet transmission is completely secure. We encourage you
                to use strong passwords and protect your account credentials.
              </p>

              {/* 5 */}
              <h2 className="mt-6 text-xl font-medium">5. DATA RETENTION</h2>
              <p className="mt-2">
                We retain your information for as long as necessary to provide our Services and comply
                with legal obligations. If you delete your account, we may retain certain information
                if required by law or for legitimate business purposes.
              </p>

              {/* 6 */}
              <h2 className="mt-6 text-xl font-medium">6. YOUR RIGHTS AND CHOICES</h2>
              <p className="mt-2">Depending on your location, you may have rights to:</p>
              <ul className="list-disc pl-6">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Delete your information</li>
                <li>Opt-out of marketing communications</li>
                <li>Export your data</li>
              </ul>
              <p className="mt-2">
                To exercise these rights, contact us at{" "}
                <a href="mailto:mrjoe@mrjoe.com" className="inline-block hover:underline">
                  mrjoe@mrjoe.com
                </a>
              </p>

              {/* 7 */}
              <h2 className="mt-6 text-xl font-medium">7. CHILDREN&apos;S PRIVACY</h2>
              <p className="mt-2">
                Our Services are not directed to children under 16. We do not knowingly collect
                information from children under 16. If we learn we have collected such information, we
                will delete it.
              </p>

              {/* 8 */}
              <h2 className="mt-6 text-xl font-medium">8. CHANGES TO THIS POLICY</h2>
              <p className="mt-2">
                We may update this Policy periodically. We will notify you of material changes through
                the Services or by email. Your continued use of the Services after changes indicates
                your acceptance of the updated Policy.
              </p>

              {/* 9 */}
              <h2 className="mt-6 text-xl font-medium">9. CONTACT US</h2>
              <p className="mt-2">
                For questions about this Policy or our privacy practices, contact us at:
              </p>
              <p>
                Email:{" "}
                <a href="mailto:mrjoe@mrjoe.com" className="inline-block hover:underline">
                  mrjoe@mrjoe.com
                </a>
              </p>

              {/* 10 */}
              <h2 className="mt-6 text-xl font-medium">10. NOTICE TO CALIFORNIA RESIDENTS</h2>
              <p className="mt-2">
                If you are a California resident, you have additional rights under the California
                Consumer Privacy Act (CCPA), including:
              </p>
              <ul className="list-disc pl-6">
                <li>Right to know what personal information we collect</li>
                <li>Right to delete your personal information</li>
                <li>Right to opt-out of the sale of your personal information</li>
                <li>Right to non-discrimination for exercising your rights</li>
              </ul>

              {/* 11 */}
              <h2 className="mt-6 text-xl font-medium">11. COOKIES AND TRACKING</h2>
              <p className="mt-2">We use cookies and similar technologies to:</p>
              <ul className="list-disc pl-6">
                <li>Remember your preferences</li>
                <li>Understand how you use our Services</li>
                <li>Improve our Services</li>
                <li>Provide targeted advertising</li>
              </ul>
              <p className="mt-2">
                You can control cookies through your browser settings, but this may affect the
                functionality of our Services.
              </p>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}